import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

// 🔹 Reusable Try It Button
function TryItButton({ code }) {
  const handleClick = () => {
    const encodedCode = encodeURIComponent(code);
    window.open(`/editorJS?code=${encodedCode}`, "_blank");
  };

  return (
    <button onClick={handleClick} className="try_it_button">
      ▶ Try It
    </button>
  );
}

export default function JS_Let() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">💡 JavaScript let Keyword</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        The <b>let</b> keyword was introduced in <b>ES6 (2015)</b> and is now
        the preferred way to declare variables in JavaScript. It provides{" "}
        <b>block scope</b>, which prevents many of the problems caused by{" "}
        <code>var</code>.
      </p>

      {/* Key Points */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #f0fdf4, #dcfce7)",
        }}
      >
        <h2 style={{ color: "#166534" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            <code>let</code> is <b>block scoped</b>.
          </li>
          <li>
            It can be <b>updated</b> but not <b>redeclared</b> in the same
            scope.
          </li>
          <li>
            Variables declared with <code>let</code> are <b>not hoisted</b> like{" "}
            <code>var</code>.
          </li>
          <li>
            Use <code>let</code> for variables that will change.
          </li>
        </ul>
      </div>

      {/* Example 1: Basic let Declaration */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Basic let Declaration</h2>
        <p>
          You can declare a variable using <code>let</code> and assign a value
          that can be updated later.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let name = "Edu-Tech Master Academy";
console.log(name);
name = "ETMA JavaScript Course";
console.log(name);`}
          />
          <TryItButton
            code={`let name = "Edu-Tech Master Academy";
console.log(name);
name = "ETMA JavaScript Course";
console.log(name);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #22c55e",
            borderRadius: "8px",
          }}
        >
          Output:
          <pre>
            Edu-Tech Master Academy
            <br />
            ETMA JavaScript Course
          </pre>
        </div>
      </div>

      {/* Example 2: Block Scope */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ Block Scope</h2>
        <p>
          A variable declared with <code>let</code> is only available inside the{" "}
          <code>{`{}`}</code> block it was defined in.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let name = "Edu-Tech Master Academy";

{
  let name = "ETMA Inside Block";
  console.log(name);
}

console.log(name);`}
          />
          <TryItButton
            code={`let name = "Edu-Tech Master Academy";

{
  let name = "ETMA Inside Block";
  console.log(name);
}

console.log(name);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #3b82f6",
            borderRadius: "8px",
          }}
        >
          Output:
          <pre>
            ETMA Inside Block
            <br />
            Edu-Tech Master Academy
          </pre>
        </div>
      </div>

      {/* Example 3: No Redeclaration */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ No Redeclaration</h2>
        <p>
          You cannot redeclare a variable with <code>let</code> in the same
          scope.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let name = "Edu-Tech Master Academy";
let name = "ETMA JavaScript"; // ❌ Error`}
          />
          <TryItButton
            code={`let name = "Edu-Tech Master Academy";
let name = "ETMA JavaScript";`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #f59e0b",
            borderRadius: "8px",
          }}
        >
          Output: ❌ SyntaxError — Identifier 'name' has already been declared.
        </div>
      </div>

      {/* Example 4: Not Hoisted */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ Not Hoisted</h2>
        <p>
          Variables declared with <code>let</code> are not accessible before
          their declaration due to the <b>temporal dead zone</b>.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`console.log(name); // ❌ Error
let name = "Edu-Tech Master Academy";
console.log(name);`}
          />
          <TryItButton
            code={`console.log(name);
let name = "Edu-Tech Master Academy";
console.log(name);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #ef4444",
            borderRadius: "8px",
          }}
        >
          Output: ❌ ReferenceError — Cannot access 'name' before
          initialization.
        </div>
      </div>

      {/* Example 5: Reassignment */}
      <div className="tutorial-section">
        <h2 className="section-title">5️⃣ Reassignment</h2>
        <p>
          You can change (reassign) a value of a variable declared with{" "}
          <code>let</code>.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let course = "JavaScript Basics";
course = "Advanced JavaScript";
console.log(course);`}
          />
          <TryItButton
            code={`let course = "JavaScript Basics";
course = "Advanced JavaScript";
console.log(course);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #16a34a",
            borderRadius: "8px",
          }}
        >
          Output: <b>Advanced JavaScript</b>
        </div>
      </div>

      {/* Best Practices */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🧠 Best Practices</h2>
        <ul className="tutorial-list-points">
          <li>
            ✅ Use <code>let</code> for variables that change values.
          </li>
          <li>✅ Always declare variables at the top of the block.</li>
          <li>
            ⚠ Avoid <code>var</code> — use <code>let</code> or{" "}
            <code>const</code> instead.
          </li>
        </ul>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Learn about <b>const</b> and how it differs from <code>let</code>.
          </li>
          <li>
            Understand <b>JavaScript Scope</b> and <b>Hoisting</b> deeper.
          </li>
          <li>
            Try <b>interactive examples</b> in the Edu-Tech Editor.
          </li>
        </ul>
      </div>
    </div>
  );
}
